import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, List, X, XCircle } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { StatCard } from '@ooh/ui/components/stat-card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@ooh/ui/components/table';
import { importEndpoints } from '@ooh/contracts';
import type { BulkPhotoImportResultRow, BulkPhotoImportResultSummary } from '@ooh/contracts';
import { Link } from '../../plugs/link';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { translatePhotoResultMessage } from '../lib/photo-result-messages';

// Helper: treat errors-endpoint rows as BulkPhotoImportResultRow for photo-bundle jobs.
// The worker writes rows with severity 'success'|'warning'|'error'; we cast at the boundary.
function toPhotoRows(
  rows: {
    row: number;
    field: string;
    message: string;
    code?: string;
    params?: Record<string, string>;
    severity?: string;
  }[],
): BulkPhotoImportResultRow[] {
  return rows.map((r) => ({
    row: r.row,
    field: r.field,
    message: r.message,
    code: r.code,
    params: r.params,
    severity: (r.severity ?? 'error') as BulkPhotoImportResultRow['severity'],
  }));
}

interface PhotoBundleResultTableProps {
  rows: BulkPhotoImportResultRow[];
}

function severityIcon(severity: string) {
  if (severity === 'success') return <Check className="h-4 w-4 text-emerald-600" />;
  if (severity === 'warning') return <span className="text-amber-600">⚠</span>;
  return <X className="h-4 w-4 text-red-600" />;
}

function PhotoBundleResultTable({ rows }: PhotoBundleResultTableProps) {
  const { t } = useTranslation('mediaImport');
  return (
    <div
      className="rounded-lg border border-border overflow-hidden"
      data-testid="photo-bundle-result-table"
    >
      <Table className="text-[13px]">
        <TableHeader>
          <TableRow className="bg-muted/40">
            <TableHead>{t('result.photoBundleTableHeaderFile')}</TableHead>
            <TableHead>{t('result.photoBundleTableHeaderStatus')}</TableHead>
            <TableHead>{t('result.photoBundleTableHeaderMessage')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.row}-${row.field}`}>
              <TableCell>{row.field}</TableCell>
              <TableCell>{severityIcon(row.severity)}</TableCell>
              <TableCell className="text-muted-foreground">
                {translatePhotoResultMessage(row, t)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function PhotoBundleStats({
  rows,
  uploadedCount,
}: {
  rows: BulkPhotoImportResultRow[];
  uploadedCount: number;
}) {
  const { t } = useTranslation('mediaImport');
  const warnings = rows.filter((r) => r.severity === 'warning').length;
  const errors = rows.filter((r) => r.severity === 'error').length;
  return (
    <div className="flex gap-3 flex-wrap">
      <StatCard
        icon={Check}
        label={t('result.statUploaded')}
        value={uploadedCount}
        loading={false}
        tone="success"
        testId="stat-uploaded"
      />
      {warnings > 0 && (
        <StatCard
          icon={AlertTriangle}
          label={t('result.statWarnings')}
          value={warnings}
          loading={false}
          tone="warning"
        />
      )}
      {errors > 0 && (
        <StatCard
          icon={XCircle}
          label={t('result.statErrors')}
          value={errors}
          loading={false}
          tone="destructive"
        />
      )}
    </div>
  );
}

// Always visible (inline, no toggle) whenever noMediaUnitCount > 0. Sourced from the
// job result summary, not derived from per-row messages.

function UnmatchedPhotosCallout({ summary }: { summary: BulkPhotoImportResultSummary }) {
  const { t } = useTranslation('mediaImport');
  return (
    <div
      className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 space-y-2"
      data-testid="unmatched-photos-callout"
    >
      <p className="text-sm font-medium text-amber-800">
        {t('result.unmatchedCallout', { count: summary.noMediaUnitCount })}
      </p>
      {summary.unmatchedSupplierNumbers.length > 0 && (
        <ul className="flex flex-wrap gap-1.5 text-xs" data-testid="unmatched-supplier-list">
          {summary.unmatchedSupplierNumbers.map((supplierNumber) => (
            <li
              key={supplierNumber}
              className="rounded bg-amber-100 px-2 py-0.5 font-mono text-amber-900"
            >
              {supplierNumber}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * CU-86cak37vt: never report success when zero photos were actually uploaded — job.status
 * === 'completed' only means the worker finished, not that anything matched. Exported for
 * unit testing.
 */
export function isPhotoBundleResultFailed(
  jobStatus: string | undefined,
  uploadedCount: number,
): boolean {
  return jobStatus === 'failed' || uploadedCount === 0;
}

// Handles its own data fetching to keep ResultPhase complexity within limits.

interface PhotoBundleResultPhaseProps {
  importJobId: number;
  jobStatus?: string;
  resultSummary?: BulkPhotoImportResultSummary;
  onBackToImport?: () => void;
}

export function PhotoBundleResultPhase({
  importJobId,
  jobStatus,
  resultSummary,
  onBackToImport,
}: PhotoBundleResultPhaseProps) {
  const { t } = useTranslation('mediaImport');

  // Fetch per-photo result rows from the errors endpoint.
  // The status endpoint does not include errorLog in its response payload.
  const { data: errorsResponse } = useApiSuspenseQuery(
    importEndpoints.getErrors,
    { id: importJobId },
    { staleTime: Infinity },
  );

  const photoRows = toPhotoRows(errorsResponse.errors);
  const uploadedCount = photoRows.filter((r) => r.severity === 'success').length;
  const isFailed = isPhotoBundleResultFailed(jobStatus, uploadedCount);
  const headingBg = isFailed ? 'bg-red-100' : 'bg-emerald-100';
  const iconColor = isFailed ? 'text-red-600' : 'text-emerald-600';
  const heading = isFailed ? t('result.headingWithErrors') : t('result.photosBundleSuccess');

  return (
    <div className="max-w-xl mx-auto space-y-6 py-6" data-testid="phase-result">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className={`flex h-16 w-16 items-center justify-center rounded-full ${headingBg}`}>
          {isFailed ? (
            <X className={`h-8 w-8 ${iconColor}`} />
          ) : (
            <Check className={`h-8 w-8 ${iconColor}`} />
          )}
        </div>
        <div>
          <p className="text-[17px] font-semibold">{heading}</p>
        </div>
      </div>

      <PhotoBundleStats rows={photoRows} uploadedCount={uploadedCount} />
      {resultSummary !== undefined && resultSummary.noMediaUnitCount > 0 && (
        <UnmatchedPhotosCallout summary={resultSummary} />
      )}
      <PhotoBundleResultTable rows={photoRows} />

      <div className="flex flex-col gap-3">
        {/* Parity with XlsxResultPhase's CTA set (CU-86cb1f27y): primary "view
            imported" linking to the catalog filtered by this import job — the
            worker records import_job_media_units for every unit that received
            a photo, so the importJobId filter shows exactly those units. */}
        <Link to={`/media-units?importJobId=${importJobId}`} data-testid="view-imported-items">
          <Button type="button" fullWidth>
            <List className="h-4 w-4" />
            {t('result.viewImportedButton')}
          </Button>
        </Link>
        <Link to="/media-units" data-testid="go-to-inventory">
          <Button type="button" variant="outline" fullWidth>
            {t('result.goToInventory')}
          </Button>
        </Link>
        {onBackToImport !== undefined && (
          <Button
            type="button"
            variant="ghost"
            fullWidth
            data-testid="back-to-import"
            onClick={onBackToImport}
          >
            {t('result.backToImport')}
          </Button>
        )}
      </div>
    </div>
  );
}
